interface Props {
  data: unknown
  title?: string
}

export function MarkdownResult({ data, title }: Props) {
  const formatValue = (value: unknown): string => {
    try {
      if (value === null) return '*null*'
      if (value === undefined) return '*undefined*'
      if (typeof value === 'string') return value
      if (typeof value === 'number' || typeof value === 'boolean') return String(value)
      if (Array.isArray(value)) {
        return value.map((item) => `- ${formatValue(item)}`).join('\n')
      }
      if (typeof value === 'object') {
        return Object.entries(value as Record<string, unknown>)
          .map(([key, val]) => `**${key}**: ${formatValue(val)}`)
          .join('\n')
      }
      return String(value)
    } catch (error) {
      console.error('Error formatting value:', error)
      return '*Error formatting value*'
    }
  }

  const generateMarkdown = (obj: unknown): string => {
    if (Array.isArray(obj)) {
      if (obj.length === 0) return '*No items*'

      // Check if it's an array of objects with similar keys (table-like)
      if (obj.length > 0 && typeof obj[0] === 'object' && obj[0] !== null) {
        const firstItem = obj[0] as Record<string, unknown>
        const keys = Object.keys(firstItem)

        // Create table if objects have consistent structure
        if (obj.every((item) => typeof item === 'object' && item !== null)) {
          const headers = `| ${keys.join(' | ')} |`
          const separator = `| ${keys.map(() => '---').join(' | ')} |`
          const rows = obj.slice(0, 10).map((item) => {
            const itemObj = item as Record<string, unknown>
            const values = keys.map((key) => String(itemObj[key] ?? '').replace(/\|/g, '\\|'))
            return `| ${values.join(' | ')} |`
          })

          let table = [headers, separator, ...rows].join('\n')
          if (obj.length > 10) {
            table += `\n\n*Showing 10 of ${obj.length} items*`
          }
          return table
        }
      }

      // Fallback to list format
      return obj
        .slice(0, 20)
        .map((item) => `- ${formatValue(item)}`)
        .join('\n')
    }

    if (typeof obj === 'object' && obj !== null) {
      const entries = Object.entries(obj as Record<string, unknown>)
      return entries.map(([key, value]) => `**${key}**: ${formatValue(value)}`).join('\n\n')
    }

    return formatValue(obj)
  }

  let markdown: string
  try {
    markdown = generateMarkdown(data)
  } catch (error) {
    console.error('Error generating markdown:', error)
    markdown = `*Error displaying data: ${error instanceof Error ? error.message : 'Unknown error'}*\n\n**Raw data:**\n${JSON.stringify(data, null, 2)}`
  }

  return (
    <div className="space-y-3">
      {title && <h3 className="text-lg font-semibold">{title}</h3>}
      <div className="prose prose-sm max-w-none">
        <pre className="whitespace-pre-wrap rounded border bg-gray-50 p-4 font-mono text-sm">
          {markdown}
        </pre>
      </div>
    </div>
  )
}
